import { hashString } from '../core/rng';
import type { SkillDef } from '../sim/skillTypes';
import { STATUSES } from '../sim/status';

/**
 * Procedural icons. Each skill icon is painted from its mechanics (shape,
 * cast type, element) in the class hue, so every skill gets a distinct,
 * original icon without any image assets.
 */

const cache = new Map<string, string>();
const SIZE = 64;

type Glyph = 'crescent' | 'shockwave' | 'impact' | 'lance' | 'orb' | 'meteor' | 'spiral' | 'charge' | 'dash' | 'buff' | 'star' | 'claw' | 'bolt' | 'shield' | 'fist' | 'note';

function hexToRgb(h: number): [number, number, number] {
  return [(h >> 16) & 255, (h >> 8) & 255, h & 255];
}
const rgba = (c: [number, number, number], a: number, k = 1) => `rgba(${Math.min(255, c[0] * k) | 0},${Math.min(255, c[1] * k) | 0},${Math.min(255, c[2] * k) | 0},${a})`;

const ELEMENT_HUE: Record<string, number> = { fire: 0xff6a2a, water: 0x6fd6ff, lightning: 0x9ab8ff, earth: 0xc8a060, holy: 0xffe28a, dark: 0xb060ff };

export function glyphFor(s: SkillDef): Glyph {
  if (s.kind === 'awakening') return 'star';
  if (s.kind === 'dash') return 'dash';
  if (s.icon && GLYPHS.includes(s.icon as Glyph)) return s.icon as Glyph;
  if (s.castType === 'holding') return 'spiral';
  if (s.castType === 'charge') return 'charge';
  const acts = [...(s.preStage?.actions ?? []), ...s.stages.flatMap((st) => st.actions), ...(s.holding?.tick ?? [])];
  if (acts.some((a) => a.kind === 'party' || a.kind === 'self') && !acts.some((a) => a.kind === 'hit' && a.hit.coef > 2)) return 'buff';
  if (acts.some((a) => a.kind === 'projectile')) return s.element === 'lightning' ? 'bolt' : 'orb';
  const area = acts.find((a) => a.kind === 'area');
  if (area && area.kind === 'area' && (area.area.anchor === 'point' || s.castType === 'point')) return 'meteor';
  const firstHit = acts.find((a) => a.kind === 'hit');
  if (acts.some((a) => a.kind === 'move') && (!firstHit || firstHit.kind !== 'hit' || firstHit.hit.shape.kind !== 'circle')) return 'lance';
  if (firstHit && firstHit.kind === 'hit') {
    const sh = firstHit.hit.shape;
    if (sh.kind === 'cone') return 'crescent';
    if (sh.kind === 'rect') return 'lance';
    if (sh.kind === 'ring') return 'shockwave';
    if (sh.kind === 'circle') return (sh.offset ?? 0) > 0 || firstHit.hit.anchor === 'point' ? 'impact' : 'shockwave';
  }
  return 'impact';
}

const GLYPHS: Glyph[] = ['crescent', 'shockwave', 'impact', 'lance', 'orb', 'meteor', 'spiral', 'charge', 'dash', 'buff', 'star', 'claw', 'bolt', 'shield', 'fist', 'note'];

function drawGlyph(g: CanvasRenderingContext2D, glyph: Glyph, col: [number, number, number], seed: number): void {
  const c = SIZE / 2;
  const light = rgba(col, 1, 1.6);
  const white = 'rgba(255,250,235,0.95)';
  g.save();
  g.translate(c, c);
  g.rotate(((seed % 60) - 30) * (Math.PI / 180) * 0.4);
  g.lineCap = 'round';
  g.lineJoin = 'round';
  g.shadowColor = rgba(col, 1, 1.4);
  g.shadowBlur = 10;
  switch (glyph) {
    case 'crescent': {
      g.strokeStyle = white;
      g.lineWidth = 7;
      g.beginPath();
      g.arc(-4, 6, 20, -Math.PI * 0.95, -Math.PI * 0.1);
      g.stroke();
      g.strokeStyle = light;
      g.lineWidth = 3;
      g.beginPath();
      g.arc(-4, 6, 13, -Math.PI * 0.9, -Math.PI * 0.2);
      g.stroke();
      break;
    }
    case 'shockwave': {
      for (let i = 0; i < 3; i++) {
        g.strokeStyle = i === 0 ? white : light;
        g.lineWidth = 4 - i;
        g.beginPath();
        g.ellipse(0, 6, 8 + i * 8, 4 + i * 4, 0, 0, Math.PI * 2);
        g.stroke();
      }
      g.fillStyle = white;
      g.beginPath();
      g.moveTo(-5, 4);
      g.lineTo(0, -20);
      g.lineTo(5, 4);
      g.fill();
      break;
    }
    case 'impact': {
      g.fillStyle = white;
      g.beginPath();
      for (let i = 0; i < 16; i++) {
        const a = (i / 16) * Math.PI * 2;
        const r = i % 2 === 0 ? 22 : 8;
        g.lineTo(Math.cos(a) * r, Math.sin(a) * r);
      }
      g.closePath();
      g.fill();
      g.fillStyle = light;
      g.beginPath();
      g.arc(0, 0, 6, 0, Math.PI * 2);
      g.fill();
      break;
    }
    case 'lance': {
      g.strokeStyle = white;
      g.lineWidth = 5;
      g.beginPath();
      g.moveTo(-20, 20);
      g.lineTo(16, -16);
      g.stroke();
      g.fillStyle = white;
      g.beginPath();
      g.moveTo(22, -22);
      g.lineTo(8, -14);
      g.lineTo(14, -8);
      g.fill();
      g.strokeStyle = light;
      g.lineWidth = 2;
      for (let i = 0; i < 3; i++) {
        g.beginPath();
        g.moveTo(-24 + i * 6, 10 + i * 6);
        g.lineTo(-12 + i * 6, -2 + i * 6);
        g.stroke();
      }
      break;
    }
    case 'orb':
    case 'bolt': {
      if (glyph === 'bolt') {
        g.fillStyle = white;
        g.beginPath();
        g.moveTo(6, -24);
        g.lineTo(-10, 2);
        g.lineTo(0, 2);
        g.lineTo(-6, 24);
        g.lineTo(12, -4);
        g.lineTo(2, -4);
        g.closePath();
        g.fill();
      } else {
        g.strokeStyle = light;
        g.lineWidth = 3;
        for (let i = 0; i < 3; i++) {
          g.beginPath();
          g.moveTo(-24, 8 - i * 7);
          g.lineTo(-2, 2 - i * 4);
          g.stroke();
        }
        const grd = g.createRadialGradient(6, -4, 1, 6, -4, 14);
        grd.addColorStop(0, 'white');
        grd.addColorStop(1, rgba(col, 0.2, 1.5));
        g.fillStyle = grd;
        g.beginPath();
        g.arc(6, -4, 13, 0, Math.PI * 2);
        g.fill();
      }
      break;
    }
    case 'meteor': {
      g.strokeStyle = light;
      g.lineWidth = 6;
      g.beginPath();
      g.moveTo(-22, -22);
      g.lineTo(2, 2);
      g.stroke();
      const grd = g.createRadialGradient(6, 6, 1, 6, 6, 12);
      grd.addColorStop(0, 'white');
      grd.addColorStop(1, rgba(col, 0.3, 1.5));
      g.fillStyle = grd;
      g.beginPath();
      g.arc(6, 6, 11, 0, Math.PI * 2);
      g.fill();
      g.strokeStyle = white;
      g.lineWidth = 2;
      g.beginPath();
      g.ellipse(6, 20, 18, 5, 0, 0, Math.PI * 2);
      g.stroke();
      break;
    }
    case 'spiral': {
      g.strokeStyle = white;
      g.lineWidth = 4;
      g.beginPath();
      for (let t = 0; t < 14; t += 0.2) {
        const r = 2 + t * 1.6;
        g.lineTo(Math.cos(t) * r, Math.sin(t) * r);
      }
      g.stroke();
      break;
    }
    case 'charge': {
      g.fillStyle = white;
      g.beginPath();
      g.moveTo(0, -24);
      g.lineTo(16, 0);
      g.lineTo(6, 0);
      g.lineTo(6, 22);
      g.lineTo(-6, 22);
      g.lineTo(-6, 0);
      g.lineTo(-16, 0);
      g.closePath();
      g.fill();
      break;
    }
    case 'dash': {
      g.strokeStyle = white;
      g.lineWidth = 5;
      for (let i = 0; i < 3; i++) {
        g.beginPath();
        g.moveTo(-18 + i * 12, -14);
        g.lineTo(-6 + i * 12, 0);
        g.lineTo(-18 + i * 12, 14);
        g.stroke();
      }
      break;
    }
    case 'buff':
    case 'shield': {
      g.fillStyle = white;
      g.beginPath();
      g.moveTo(0, -22);
      g.quadraticCurveTo(18, -18, 18, -4);
      g.quadraticCurveTo(16, 14, 0, 22);
      g.quadraticCurveTo(-16, 14, -18, -4);
      g.quadraticCurveTo(-18, -18, 0, -22);
      g.fill();
      g.fillStyle = rgba(col, 1, 1.2);
      g.beginPath();
      g.moveTo(0, -12);
      g.lineTo(8, 4);
      g.lineTo(0, 0);
      g.lineTo(-8, 4);
      g.closePath();
      g.fill();
      break;
    }
    case 'star': {
      g.fillStyle = white;
      g.beginPath();
      for (let i = 0; i < 10; i++) {
        const a = (i / 10) * Math.PI * 2 - Math.PI / 2;
        const r = i % 2 === 0 ? 26 : 10;
        g.lineTo(Math.cos(a) * r, Math.sin(a) * r);
      }
      g.closePath();
      g.fill();
      g.strokeStyle = 'rgba(255,215,120,0.9)';
      g.lineWidth = 2;
      g.beginPath();
      g.arc(0, 0, 28, 0, Math.PI * 2);
      g.stroke();
      break;
    }
    case 'claw': {
      g.strokeStyle = white;
      g.lineWidth = 4;
      for (let i = -1; i <= 1; i++) {
        g.beginPath();
        g.moveTo(-14 + i * 8, -18);
        g.quadraticCurveTo(i * 8, 0, 10 + i * 8, 20);
        g.stroke();
      }
      break;
    }
    case 'fist': {
      g.fillStyle = white;
      g.fillRect(-12, -10, 24, 20);
      g.fillStyle = rgba(col, 1, 0.7);
      for (let i = 0; i < 4; i++) g.fillRect(-11 + i * 6, -9, 4, 7);
      g.strokeStyle = light;
      g.lineWidth = 3;
      for (let i = 0; i < 3; i++) {
        g.beginPath();
        g.moveTo(16, -12 + i * 12);
        g.lineTo(26, -12 + i * 12);
        g.stroke();
      }
      break;
    }
    case 'note': {
      g.fillStyle = white;
      g.beginPath();
      g.ellipse(-6, 12, 8, 6, -0.4, 0, Math.PI * 2);
      g.fill();
      g.fillRect(0, -20, 4, 32);
      g.beginPath();
      g.moveTo(4, -20);
      g.quadraticCurveTo(18, -14, 12, -2);
      g.lineTo(4, -8);
      g.fill();
      break;
    }
  }
  g.restore();
}

function paint(key: string, hue: number, glyph: Glyph, opts: { frame?: string; seed?: number } = {}): string {
  const hit = cache.get(key);
  if (hit) return hit;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = SIZE;
  const g = canvas.getContext('2d');
  if (!g) return '';
  const col = hexToRgb(hue);
  const seed = opts.seed ?? hashString(key);
  const bg = g.createRadialGradient(SIZE * 0.35, SIZE * 0.3, 2, SIZE * 0.5, SIZE * 0.5, SIZE * 0.75);
  bg.addColorStop(0, rgba(col, 1, 0.9));
  bg.addColorStop(0.55, rgba(col, 1, 0.38));
  bg.addColorStop(1, rgba(col, 1, 0.1));
  g.fillStyle = bg;
  g.fillRect(0, 0, SIZE, SIZE);
  // Painterly streaks.
  for (let i = 0; i < 18; i++) {
    const r = ((seed >> (i % 16)) & 255) / 255;
    g.strokeStyle = rgba(col, 0.08 + r * 0.08, 1.6);
    g.lineWidth = 1 + r * 3;
    g.beginPath();
    g.moveTo(r * SIZE, 0);
    g.lineTo(SIZE - r * SIZE * 0.5, SIZE);
    g.stroke();
  }
  drawGlyph(g, glyph, col, seed);
  // Vignette + frame.
  const v = g.createRadialGradient(SIZE / 2, SIZE / 2, SIZE * 0.3, SIZE / 2, SIZE / 2, SIZE * 0.72);
  v.addColorStop(0, 'rgba(0,0,0,0)');
  v.addColorStop(1, 'rgba(0,0,0,0.55)');
  g.fillStyle = v;
  g.fillRect(0, 0, SIZE, SIZE);
  g.strokeStyle = opts.frame ?? 'rgba(0,0,0,0.9)';
  g.lineWidth = 2;
  g.strokeRect(1, 1, SIZE - 2, SIZE - 2);
  const url = canvas.toDataURL();
  cache.set(key, url);
  return url;
}

export function skillIcon(s: SkillDef, classHue = 0x8888aa): string {
  const hue = s.element && s.element !== 'none' ? (ELEMENT_HUE[s.element] ?? classHue) : (s.hue ?? classHue);
  return paint(`skill:${s.id}`, hue, glyphFor(s), { frame: s.kind === 'awakening' ? '#d4a13a' : undefined });
}

export function statusIcon(id: string): string {
  const def = STATUSES.get(id);
  const hue = def?.color ?? 0x999999;
  const glyph: Glyph = def?.kind === 'debuff' ? (def.tick ? 'impact' : def.cc ? 'spiral' : 'claw') : def?.tick ? 'buff' : id.includes('shield') || id === 'guard' ? 'shield' : 'charge';
  return paint(`status:${id}`, hue, glyph);
}

export function genericIcon(key: string, hue: number, glyph: Glyph): string {
  return paint(`generic:${key}`, hue, glyph);
}
